import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { getPageviewData } from '../../utils/tracking/searchBrowse/getPageviewData';
import get from 'lodash.get';
import { getPublisherTagInstance } from '../../utils/getPublisherTagInstance';
import { trackSortEvent } from '../../utils/tracking/searchBrowse/trackSortEvent';
import { cdnTestTracking } from '../../utils/tracking/searchBrowse/cdnTestTracking';
import { addPersonalizationTrackingData } from '../../utils/tracking/searchBrowse/addPersonalizationTrackingData';
import { trackNullResultsDisplayed } from '../../utils/tracking/searchBrowse/nullResultsTracking';
import { SharedTrackingContainer } from '../../shared/SharedTrackingContainer';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import tracking from 'dibs-tracking';
import { SharedSaveSearch } from '../../mutations/SharedSaveSearch';
const { SEARCH } = pageTypeConstants;

class SbSharedTrackingContainerComponent extends React.Component {
    constructor() {
        super();

        this.handleTrackingFired = this.handleTrackingFired.bind(this);

        this.state = {
            visitCount: 0,
        };
    }

    componentWillReceiveProps(nextProps) {
        const hasDisplayUriRefChanged = this.hasPropChanged(
            nextProps,
            'searchBrowse.displayUriRef'
        );
        const hasUriRefChanged = this.hasPropChanged(nextProps, 'generatedUriRef');
        const sortValuePath = 'searchBrowse.sort.currentOption.urlLabel';
        const hasSortValueChanged = this.hasPropChanged(nextProps, sortValuePath);

        /**
         * We're expecting that this will run before any components get notified of a search
         * browse page change.
         */
        if (hasUriRefChanged) {
            const gt = getPublisherTagInstance();
            tracking.resetGtmTracking();

            // FINDING-13256 reset tracking fired in DBL when URL updates w/o hard refresh
            this.props.reinitTracking();

            if (gt) {
                gt.pubads().refresh();
            }
        }

        if (hasDisplayUriRefChanged) {
            this.setState(prevState => {
                return { visitCount: ++prevState.visitCount };
            });
        }

        if (hasSortValueChanged) {
            trackSortEvent(get(nextProps, sortValuePath));
        }
    }

    trackNullResultsPage() {
        const totalResults = get(this.props, 'searchBrowse.totalResults');

        if (totalResults === 0) {
            trackNullResultsDisplayed();
        }
    }

    trackUserSearchHistory() {
        const userId = get(this.props, 'user.serviceId');
        const pageType = get(this.props, 'searchBrowse.pageType');
        const displayUriRef = get(this.props, 'searchBrowse.displayUriRef');
        const totalResults = get(this.props, 'searchBrowse.totalResults');

        const hasResults = totalResults > 0;
        const isSearch = pageType === SEARCH;

        if (isSearch && userId && hasResults) {
            SharedSaveSearch.commit(this.props.relay.environment, {
                type: 'SEARCH_HISTORY',
                page: displayUriRef,
                userId,
            });
        }
    }

    handleTrackingFired() {
        const appliedFilters = get(this.props, 'searchBrowse.appliedFilters');

        addPersonalizationTrackingData(appliedFilters);

        this.trackNullResultsPage();

        this.trackUserSearchHistory();
    }

    hasPropChanged(newProps, propName) {
        const oldProp = get(this.props, propName);
        const newProp = get(newProps, propName);
        return oldProp && oldProp !== newProp;
    }

    getGtmPageInfo() {
        const { searchBrowse, pageNumber, gtmPageInfo = {}, refineMenuFiltersMap } = this.props;
        const { pageType, totalResults, appliedFilters, creatorContent } = searchBrowse;

        const isModule = get(creatorContent, 'description');
        return {
            ...gtmPageInfo,
            additional: {
                ...gtmPageInfo.additional,
                ...getPageviewData({
                    pageType,
                    appliedFilters,
                    totalResults,
                    pageNumber,
                    optPageURL: get(this.props, 'searchBrowse.displayUriRef'),
                    sortValue: get(this.props, 'searchBrowse.sort.currentOption.urlLabel'),
                    isModule,
                    refineMenuFiltersMap,
                    topCategoryL1: get(this.props, 'searchBrowse.topCategoryL1'),
                }),
            },
        };
    }

    render() {
        const additional = {
            ...this.props.additional,
            ...cdnTestTracking(),
        };

        return (
            <SharedTrackingContainer
                visitCount={this.state.visitCount}
                onTrackingFired={this.handleTrackingFired}
                gtmPageInfo={this.getGtmPageInfo()}
                user={this.props.user}
                currency={this.props.currency}
                additional={additional}
                item={null}
            />
        );
    }
}

const mapStateToProps = ({ filters }) => {
    const { generatedUriRef, page } = filters;
    return { pageNumber: page, generatedUriRef };
};

SbSharedTrackingContainerComponent.propTypes = {
    relay: PropTypes.object.isRequired,
    user: PropTypes.object,
    currency: PropTypes.string.isRequired,
    searchBrowse: PropTypes.object.isRequired,
    gtmPageInfo: PropTypes.object,
    refineMenuFiltersMap: PropTypes.object.isRequired,
    pageNumber: PropTypes.number.isRequired,
    additional: PropTypes.object,
    // TODO INFRA: Should be required
    generatedUriRef: PropTypes.string,
    reinitTracking: PropTypes.func,
};

SbSharedTrackingContainerComponent.defaultProps = {
    reinitTracking: () => {},
};

export const SbSharedTrackingContainer = createFragmentContainer(
    connect(mapStateToProps)(SbSharedTrackingContainerComponent),
    {
        searchBrowse: graphql`
            fragment SbSharedTrackingContainer_searchBrowse on SearchBrowseQueryConnection {
                topCategoryL1
                pageType
                totalResults
                displayUriRef
                browseContentModule(contentModuleId: $contentModuleId) {
                    description
                }
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
                sort {
                    currentOption {
                        urlLabel
                    }
                }
            }
        `,
        user: graphql`
            fragment SbSharedTrackingContainer_user on User {
                serviceId
                ...SharedTrackingContainer_user
            }
        `,
    }
);
